import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NAVIGATION_ITEMS } from '@/lib/config/navigation';

interface Shortcut {
  keys: string; // e.g. "ctrl+k", "g f", "ctrl+shift+n"
  description: string;
  category: 'navigation' | 'actions' | 'social' | 'tools';
  superAdminOnly?: boolean;
  adminOnly?: boolean;
  sellerOnly?: boolean;
  action: () => void;
}

interface CommandPaletteItem {
  id: string;
  label: string;
  description: string;
  category: string;
  icon?: string;
  keywords?: string[];
  path?: string;
  superAdminOnly?: boolean;
  adminOnly?: boolean;
  sellerOnly?: boolean;
  action: () => void;
  score?: number;
  isRecent?: boolean;
  isContextual?: boolean;
  isFavorite?: boolean;
}

interface KeyboardShortcutOptions {
  currentPath?: string;
  prefetchPath?: (path: string) => void;
  isSuperAdmin?: boolean;
  isAdminUser?: boolean;
  isSellerUser?: boolean;
}

const RECENT_COMMANDS_STORAGE_KEY = 'intouch-recent-commands-v1';
const FAVORITE_COMMANDS_STORAGE_KEY = 'intouch-favorite-commands-v1';
const COMMAND_USAGE_STORAGE_KEY = 'intouch-command-usage-v1';
const MAX_RECENT_COMMANDS = 8;

interface CommandUsageStat {
  count: number;
  lastUsedAt: number;
}

function normalizeText(value: string): string {
  return value.toLowerCase().trim();
}

function tokenize(value: string): string[] {
  return normalizeText(value).split(/\s+/).filter(Boolean);
}

function isSubsequence(needle: string, haystack: string): boolean {
  if (!needle) return true;
  let i = 0;
  let j = 0;

  while (i < needle.length && j < haystack.length) {
    if (needle[i] === haystack[j]) i += 1;
    j += 1;
  }

  return i === needle.length;
}

function scoreCommand(query: string, item: CommandPaletteItem): number {
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) {
    let base = 18;
    if (item.isRecent) base += 26;
    if (item.isContextual) base += 22;
    return base;
  }

  const searchable = `${item.description} ${item.label} ${(item.keywords || []).join(' ')}`;
  const normalized = normalizeText(searchable);
  const tokens = tokenize(normalized);
  const queryTokens = tokenize(normalizedQuery);

  let score = 0;

  if (normalized === normalizedQuery) score += 140;
  if (normalized.startsWith(normalizedQuery)) score += 110;
  if (normalized.includes(normalizedQuery)) score += 85;

  for (const token of tokens) {
    if (token === normalizedQuery) score += 35;
    else if (token.startsWith(normalizedQuery)) score += 24;
    else if (token.includes(normalizedQuery)) score += 12;
  }

  for (const token of queryTokens) {
    if (!token) continue;
    if (tokens.some((word) => word.startsWith(token))) {
      score += 22;
      continue;
    }
    if (normalized.includes(token)) {
      score += 10;
    }
  }

  if (isSubsequence(normalizedQuery.replace(/\s+/g, ''), normalized.replace(/\s+/g, ''))) {
    score += 18;
  }

  if (item.isRecent) score += 28;
  if (item.isContextual) score += 20;
  if (item.isFavorite) score += 34;
  return score;
}

function getPermissionFlags(item: Pick<CommandPaletteItem, 'superAdminOnly' | 'adminOnly' | 'sellerOnly'>): {
  superAdminOnly: boolean;
  adminOnly: boolean;
  sellerOnly: boolean;
} {
  return {
    superAdminOnly: !!item.superAdminOnly,
    adminOnly: !!item.adminOnly,
    sellerOnly: !!item.sellerOnly,
  };
}

function hasCommandPermission(
  item: Pick<CommandPaletteItem, 'superAdminOnly' | 'adminOnly' | 'sellerOnly'>,
  roles: { isSuperAdmin: boolean; isAdminUser: boolean; isSellerUser: boolean }
): boolean {
  const flags = getPermissionFlags(item);

  if (flags.superAdminOnly && !roles.isSuperAdmin) return false;
  if (flags.adminOnly && !roles.isAdminUser) return false;
  if (flags.sellerOnly && !roles.isSellerUser) return false;
  return true;
}

function getUsageBonus(stat?: CommandUsageStat): number {
  if (!stat || stat.count <= 0) return 0;

  const now = Date.now();
  const ageMs = Math.max(0, now - stat.lastUsedAt);
  const dayMs = 24 * 60 * 60 * 1000;
  const recencyMultiplier = ageMs < dayMs ? 1 : ageMs < 7 * dayMs ? 0.75 : ageMs < 30 * dayMs ? 0.5 : 0.3;

  return Math.log2(stat.count + 1) * 16 * recencyMultiplier;
}

function isSameAppSection(currentPath: string, itemPath?: string): boolean {
  if (!itemPath) return false;

  const currentRoot = currentPath.split('/').filter(Boolean)[0] || '/';
  const itemRoot = itemPath.split('/').filter(Boolean)[0] || '/';
  return currentRoot === itemRoot;
}

function buildRouteKeywords(path: string, label: string): string[] {
  const raw = `${path} ${label}`
    .replace(/\//g, ' ')
    .replace(/-/g, ' ')
    .toLowerCase();

  const tokens = Array.from(new Set(raw.split(/\s+/).filter(Boolean)));
  const aliasByPath: Record<string, string[]> = {
    '/': ['dashboard', 'start'],
    '/messages': ['chat', 'dm', 'inbox'],
    '/feed': ['timeline', 'posts'],
    '/notifications': ['alerts', 'activity'],
    '/wallet': ['balance', 'funds', 'crypto'],
    '/marketplace': ['shop', 'store', 'products'],
    '/events': ['calendar', 'meetups'],
    '/friends': ['contacts', 'network'],
    '/groups': ['communities'],
    '/settings': ['preferences', 'config'],
    '/ai': ['assistant', 'copilot', 'agent'],
  };

  return [...tokens, ...(aliasByPath[path] || [])];
}

function getContextualPaths(currentPath: string): string[] {
  if (currentPath.startsWith('/messages')) {
    return ['/friends', '/notifications', '/profile'];
  }

  if (currentPath.startsWith('/marketplace')) {
    return ['/my-products', '/seller/dashboard', '/wallet'];
  }

  if (currentPath.startsWith('/wallet') || currentPath.startsWith('/credit-card')) {
    return ['/crypto-atlas', '/exchange', '/staking'];
  }

  if (currentPath.startsWith('/transportation')) {
    return ['/transportation/airline', '/transportation/train', '/transportation/taxi'];
  }

  return ['/feed', '/messages', '/notifications'];
}

export function useKeyboardShortcuts(options: KeyboardShortcutOptions = {}) {
  const {
    currentPath = '/',
    prefetchPath,
    isSuperAdmin = false,
    isAdminUser = false,
    isSellerUser = false,
  } = options;
  const navigate = useNavigate();
  const [showPalette, setShowPalette] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const pressedKeys = useRef<Set<string>>(new Set());
  const [registeredShortcuts, setRegisteredShortcuts] = useState<Shortcut[]>([]);
  const [recentCommandIds, setRecentCommandIds] = useState<string[]>([]);
  const [favoriteCommandIds, setFavoriteCommandIds] = useState<string[]>([]);
  const [commandUsageStats, setCommandUsageStats] = useState<Record<string, CommandUsageStat>>({});

  const roleContext = useMemo(
    () => ({ isSuperAdmin, isAdminUser, isSellerUser }),
    [isSuperAdmin, isAdminUser, isSellerUser]
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_COMMANDS_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setRecentCommandIds(parsed.filter((entry) => typeof entry === 'string').slice(0, MAX_RECENT_COMMANDS));
      }
    } catch {
      setRecentCommandIds([]);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(FAVORITE_COMMANDS_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setFavoriteCommandIds(parsed.filter((entry) => typeof entry === 'string'));
      }
    } catch {
      setFavoriteCommandIds([]);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(COMMAND_USAGE_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setCommandUsageStats(parsed as Record<string, CommandUsageStat>);
      }
    } catch {
      setCommandUsageStats({});
    }
  }, []);

  const rememberCommand = useCallback((commandId: string) => {
    setRecentCommandIds((previous) => {
      const next = [commandId, ...previous.filter((id) => id !== commandId)].slice(0, MAX_RECENT_COMMANDS);
      try {
        localStorage.setItem(RECENT_COMMANDS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Best effort only.
      }
      return next;
    });
  }, []);

  const toggleFavoriteCommand = useCallback((commandId: string) => {
    setFavoriteCommandIds((previous) => {
      const exists = previous.includes(commandId);
      const next = exists ? previous.filter((id) => id !== commandId) : [commandId, ...previous];

      try {
        localStorage.setItem(FAVORITE_COMMANDS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Best effort only.
      }

      return next;
    });
  }, []);

  const trackCommandUsage = useCallback((commandId: string) => {
    setCommandUsageStats((previous) => {
      const current = previous[commandId];
      const nextForCommand: CommandUsageStat = {
        count: (current?.count || 0) + 1,
        lastUsedAt: Date.now(),
      };

      const next = {
        ...previous,
        [commandId]: nextForCommand,
      };

      try {
        localStorage.setItem(COMMAND_USAGE_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Best effort only.
      }

      return next;
    });
  }, []);

  // Default shortcuts
  const shortcuts: Shortcut[] = useMemo(() => [
    { keys: 'ctrl+k', description: 'Command palette', category: 'navigation', action: () => setShowPalette(true) },
    { keys: 'g f', description: 'Go to Feed', category: 'navigation', action: () => navigate('/feed') },
    { keys: 'g m', description: 'Go to Messages', category: 'navigation', action: () => navigate('/messages') },
    { keys: 'g p', description: 'Go to Profile', category: 'navigation', action: () => navigate('/profile') },
    { keys: 'g n', description: 'Go to Notifications', category: 'navigation', action: () => navigate('/notifications') },
    { keys: 'g h', description: 'Go to Home', category: 'navigation', action: () => navigate('/') },
    { keys: 'g w', description: 'Go to Wallet', category: 'navigation', action: () => navigate('/wallet') },
    { keys: 'g k', description: 'Go to Marketplace', category: 'navigation', action: () => navigate('/marketplace') },
    { keys: 'g e', description: 'Go to Events', category: 'navigation', action: () => navigate('/events') },
    { keys: 'g s', description: 'Go to Settings', category: 'navigation', action: () => navigate('/settings') },
    { keys: 'n p', description: 'New Post', category: 'actions', action: () => navigate('/create/post') },
    { keys: 'n g', description: 'New Group', category: 'actions', action: () => navigate('/create/group') },
    { keys: 'ctrl+/', description: 'Show keyboard shortcuts', category: 'tools', action: () => setShowPalette(true) },
  ], [navigate]);

  const allShortcuts = useMemo(() => [...shortcuts, ...registeredShortcuts], [shortcuts, registeredShortcuts]);
  const visibleShortcuts = useMemo(
    () => allShortcuts.filter((shortcut) => hasCommandPermission(shortcut, roleContext)),
    [allShortcuts, roleContext]
  );

  const navigationPaletteItems = useMemo<CommandPaletteItem[]>(() => {
    const contextualPaths = new Set(getContextualPaths(currentPath));

    return NAVIGATION_ITEMS
      .filter((item) => typeof item.path === 'string' && item.path.length > 0)
      .filter((item) => hasCommandPermission(item, roleContext))
      .map((item) => {
        const path = item.path;
        const active = currentPath === path || currentPath.startsWith(`${path}/`);
        const contextual = contextualPaths.has(path) || isSameAppSection(currentPath, path);

        return {
          id: `nav:${path}`,
          label: `GO ${path.toUpperCase()}`,
          description: active ? `Open ${item.label} (current)` : `Open ${item.label}`,
          category: 'navigation',
          path,
          keywords: buildRouteKeywords(path, item.label),
          isContextual: contextual,
          superAdminOnly: !!item.superAdminOnly,
          adminOnly: !!item.adminOnly,
          sellerOnly: !!item.sellerOnly,
          action: () => {
            prefetchPath?.(path);
            navigate(path);
          },
        };
      });
  }, [currentPath, navigate, prefetchPath, roleContext]);

  const utilityPaletteItems = useMemo<CommandPaletteItem[]>(() => {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    return [
      {
        id: 'tool:go-back',
        label: 'ALT+LEFT',
        description: 'Go back to previous page',
        category: 'tools',
        keywords: ['back', 'previous', 'history'],
        isContextual: true,
        action: () => window.history.back(),
      },
      {
        id: 'tool:go-forward',
        label: 'ALT+RIGHT',
        description: 'Go forward in history',
        category: 'tools',
        keywords: ['forward', 'next', 'history'],
        action: () => window.history.forward(),
      },
      {
        id: 'tool:reload',
        label: 'CMD/CTRL+R',
        description: 'Reload current page',
        category: 'tools',
        keywords: ['refresh', 'reload', 'restart'],
        isContextual: true,
        action: () => window.location.reload(),
      },
      {
        id: 'tool:copy-url',
        label: 'COPY LINK',
        description: 'Copy current URL',
        category: 'tools',
        keywords: ['copy', 'url', 'share', 'link'],
        isContextual: true,
        action: async () => {
          if (!currentUrl) return;
          try {
            await navigator.clipboard.writeText(currentUrl);
          } catch {
            // Clipboard may be unavailable in restricted browser contexts.
          }
        },
      },
      {
        id: 'tool:open-current-new-tab',
        label: 'NEW TAB',
        description: 'Open current page in a new tab',
        category: 'tools',
        keywords: ['duplicate', 'tab', 'open'],
        isContextual: true,
        action: () => {
          if (!currentUrl) return;
          window.open(currentUrl, '_blank', 'noopener,noreferrer');
        },
      },
      {
        id: 'tool:clear-recent',
        label: 'CLEAR',
        description: 'Clear recent command history',
        category: 'tools',
        keywords: ['history', 'clear', 'reset', 'recent'],
        action: () => {
          try {
            localStorage.removeItem(RECENT_COMMANDS_STORAGE_KEY);
          } catch {
            // Best effort only.
          }
          setRecentCommandIds([]);
        },
      },
    ];
  }, []);

  // Command palette items derived from shortcuts + extras
  const allPaletteItems: CommandPaletteItem[] = useMemo(() => {
    const fromShortcuts = visibleShortcuts.map((shortcut) => ({
      id: `shortcut:${shortcut.keys}`,
      label: shortcut.keys.toUpperCase(),
      description: shortcut.description,
      category: registeredShortcuts.some((candidate) => candidate.keys === shortcut.keys) ? 'custom' : shortcut.category,
      keywords: [shortcut.description.toLowerCase(), shortcut.keys.toLowerCase()],
      superAdminOnly: !!shortcut.superAdminOnly,
      adminOnly: !!shortcut.adminOnly,
      sellerOnly: !!shortcut.sellerOnly,
      action: shortcut.action,
    }));

    const dedup = new Map<string, CommandPaletteItem>();
    [...navigationPaletteItems, ...fromShortcuts, ...utilityPaletteItems].forEach((item) => {
      if (!dedup.has(item.id)) {
        dedup.set(item.id, {
          ...item,
          action: () => {
            rememberCommand(item.id);
            trackCommandUsage(item.id);
            item.action();
          },
        });
      }
    });

    return Array.from(dedup.values());
  }, [visibleShortcuts, navigationPaletteItems, registeredShortcuts, rememberCommand, utilityPaletteItems, trackCommandUsage]);

  const filteredItems = useMemo(() => {
    const withScores = allPaletteItems
      .map((item) => {
        const isRecent = recentCommandIds.includes(item.id);
        const isFavorite = favoriteCommandIds.includes(item.id);
        const contextual = !!item.isContextual || isSameAppSection(currentPath, item.path);
        const usageBonus = getUsageBonus(commandUsageStats[item.id]);
        const score = scoreCommand(paletteQuery, { ...item, isRecent, isContextual: contextual, isFavorite }) + usageBonus;
        return { ...item, isRecent, isContextual: contextual, isFavorite, score };
      })
      .filter((item) => item.score > 0);

    const recencyRank = new Map<string, number>();
    recentCommandIds.forEach((id, index) => {
      recencyRank.set(id, index);
    });

    const favoriteRank = new Map<string, number>();
    favoriteCommandIds.forEach((id, index) => {
      favoriteRank.set(id, index);
    });

    return withScores.sort((a, b) => {
      const aFavorite = favoriteRank.has(a.id);
      const bFavorite = favoriteRank.has(b.id);
      if (aFavorite !== bFavorite) {
        return aFavorite ? -1 : 1;
      }

      if (aFavorite && bFavorite) {
        return (favoriteRank.get(a.id) || 0) - (favoriteRank.get(b.id) || 0);
      }

      if ((b.score || 0) !== (a.score || 0)) {
        return (b.score || 0) - (a.score || 0);
      }

      const aRecency = recencyRank.has(a.id) ? recencyRank.get(a.id)! : Number.MAX_SAFE_INTEGER;
      const bRecency = recencyRank.has(b.id) ? recencyRank.get(b.id)! : Number.MAX_SAFE_INTEGER;
      if (aRecency !== bRecency) return aRecency - bRecency;

      return a.description.localeCompare(b.description);
    });
  }, [allPaletteItems, paletteQuery, recentCommandIds, favoriteCommandIds, commandUsageStats, currentPath]);

  const keySequenceRef = useRef<string[]>([]);
  const keySequenceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const normalizeKey = (e: KeyboardEvent): string => {
    const key = e.key.toLowerCase();
    const ctrl = e.ctrlKey || e.metaKey;
    const shift = e.shiftKey;
    const alt = e.altKey;

    const parts: string[] = [];
    if (ctrl) parts.push('ctrl');
    if (alt) parts.push('alt');
    if (shift) parts.push('shift');
    parts.push(key);

    return parts.join('+');
  };

  const findChordShortcut = (keys: string): Shortcut | undefined => {
    return visibleShortcuts.find((shortcut) => shortcut.keys.toLowerCase() === keys.toLowerCase());
  };

  const findSequenceShortcut = (sequence: string[]): Shortcut | undefined => {
    if (sequence.length === 0) return undefined;
    return visibleShortcuts.find((shortcut) => {
      const parts = shortcut.keys.toLowerCase().split(' ').filter(Boolean);
      return parts.length > 1 && parts.join(' ') === sequence.join(' ');
    });
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't capture when typing in inputs
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable
    ) {
      if (e.key !== 'Escape') return;
    }

    // Track pressed keys for sequences
    pressedKeys.current.add(e.key.toLowerCase());

    // Escape: close palette
    if (e.key === 'Escape' && showPalette) {
      e.preventDefault();
      setShowPalette(false);
      return;
    }

    // Single chord shortcuts (ctrl+k, etc.)
    const isOpenPaletteChord = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
    if (isOpenPaletteChord) {
      e.preventDefault();
      setShowPalette(true);
      return;
    }

    // Handle chord shortcuts
    const chord = normalizeKey(e);
    if (chord.includes('+')) {
      const shortcut = findChordShortcut(chord);
      if (shortcut) {
        e.preventDefault();
        rememberCommand(`shortcut:${shortcut.keys}`);
        shortcut.action();
        return;
      }
    }

    // Handle sequence shortcuts (g f, g m, etc.)
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const key = e.key.toLowerCase();
      keySequenceRef.current = [...keySequenceRef.current.slice(-1), key];

      clearTimeout(keySequenceTimerRef.current);
      keySequenceTimerRef.current = setTimeout(() => {
        keySequenceRef.current = [];
      }, 800);

      const sequenceShortcut = findSequenceShortcut(keySequenceRef.current);
      if (sequenceShortcut) {
        e.preventDefault();
        keySequenceRef.current = [];
        rememberCommand(`shortcut:${sequenceShortcut.keys}`);
        sequenceShortcut.action();
        return;
      }

      if (keySequenceRef.current.length > 1 && !findSequenceShortcut(keySequenceRef.current)) {
        keySequenceRef.current = [key];
      }
    }
  }, [visibleShortcuts, showPalette, rememberCommand]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const registerShortcut = useCallback((shortcut: Shortcut) => {
    setRegisteredShortcuts(prev => {
      const exists = prev.find(s => s.keys === shortcut.keys);
      if (exists) return prev.map(s => s.keys === shortcut.keys ? shortcut : s);
      return [...prev, shortcut];
    });
  }, []);

  const unregisterShortcut = useCallback((keys: string) => {
    setRegisteredShortcuts(prev => prev.filter(s => s.keys !== keys));
  }, []);

  return {
    shortcuts: visibleShortcuts,
    showPalette,
    setShowPalette,
    paletteQuery,
    setPaletteQuery,
    filteredItems,
    favoriteCommandIds,
    toggleFavoriteCommand,
    registerShortcut,
    unregisterShortcut,
  };
}

export default useKeyboardShortcuts;